// $FIXME: Нелегальный иммигрант, надо его депортировать из ядра
/**
 * @file LocationView.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Monday, 1st June 2020 12:33:10 pm
 * @copyright 2019 - 2020 SKAT LLC, Delive LLC
 * @flow
 */
import type { iLogger, iExposedView, iNotificationManager, iSettingsManager, CombinedValue, ReactRef } from '../../types'
import type { Point } from 'web-panel-essentials/types'
import type { LocationPoint } from 'skat-js/types'
import type { MapSpecification, PanStrategyArgs } from 'web-panel/components/types'

import * as React from 'react'
import { Row, Col, Map, PointMarker, NumericField, Action } from 'web-panel/components'
import { Inject } from '../../serviceLocator'
import { __, copyTextToClipboard, extractErrorMessage, NOTIFICATION, __mf } from 'web-panel/globals'
import { SETTINGS } from 'skat-js/constants'
import autoBind from 'react-autobind'
import { CAST } from 'web-panel-essentials/misc'
import debounce from 'lodash/debounce'
import omit from 'lodash/omit'

export type Props = {
  center: [number, number],
  point?: LocationPoint,
  cityId: number,
  serviceId: number
}

export type State = {
  point: [string, string],
  pointData: $Shape<LocationPoint>
}

const DEFAULT_FRACTION = 6
const LATITUDE_LIMITS = [-180, 180]
const LONGITUDE_LIMITS = [-90, 90]

class LocationView extends React.PureComponent<Props, State> implements iExposedView<?LocationPoint> {
  @Inject addressProvider: { reverseGeocode: Function }
  @Inject logger: iLogger
  @Inject settingsManager: iSettingsManager
  @Inject notificationManager: iNotificationManager

  latitudeInput: ReactRef<typeof NumericField>

  constructor(props: Props) {
    super(props)
    autoBind(this)
    this.latitudeInput = React.createRef()

    const point = props.point || {}
    this.state = {
      point: [
        point.latitude ? point.latitude.toFixed(DEFAULT_FRACTION) : '',
        point.longitude ? point.longitude.toFixed(DEFAULT_FRACTION) : ''
      ],
      pointData: omit(props.point, ['latitude', 'longitude'])
    }
  }

  componentDidMount() {
    this.activate()
  }

  activate() {
    this.latitudeInput.current?.focus()
  }

  async data(): Promise<?LocationPoint> {
    const { point, pointData } = this.state
    const [lat, lon] = point
    const payload = {
      ...pointData,
      latitude: CAST.Number(lat),
      longitude: CAST.Number(lon)
    }
    this.logger.info('Returning location point', payload)
    return payload
  }

  async command(commandName: string) {
    if (commandName == 'copy') await this.handleCopy()
    if (commandName == 'paste') await this.handlePaste()
  }

  handleClick(point: Point) {
    if (!this.addressProvider) { // Web-panel can't provide addressProvider by itself
      this.logger.warn('This component requires "addressProvider" with "reverseGeocode" method')
      return
    }
    const [latitude, longitude] = point
    this.setState({
      point: [
        latitude.toFixed(6),
        longitude.toFixed(6)
      ]
    })
  }

  async componentDidUpdate(prevProps: Props, prevState: State) {
    if (!this.addressProvider) { // Web-panel can't provide addressProvider by itself
      this.logger.warn('This component requires "addressProvider" with "reverseGeocode" method')
      return
    }

    const point = this.state.point
    const prevPoint = prevState.point

    if (point && (!prevPoint || prevPoint[0] !== point[0] || prevPoint[1] !== point[1])) {
      const cityId = this.props.cityId
      const serviceId = this.props.serviceId
      this.geocode(CAST.Number(point[0]), CAST.Number(point[1]), cityId, serviceId)
    }
  }

  geocode: (number, number, number, number) => void = debounce(async (latitude, longitude, cityId, serviceId) => {
    const [response] = await this.addressProvider.reverseGeocode({ where: { latitude, longitude, cityId, serviceId } })
    if (response) {
      const { street, house, regionId, markupFixed, markupPercent } = response
      // $FlowFixMe[incompatible-call]
      this.setState({
        pointData: {
          street,
          house: house || null, // undefined will be ignored
          regionId,
          markupFixed,
          markupPercent
        }
      })
    }
  }, 100)

  handleLatitudeChange({ displayValue }: CombinedValue) {
    const { point } = this.state
    this.setState({
      point: [displayValue, point[1]]
    })
  }

  handleLongitudeChange({ displayValue }: CombinedValue) {
    const { point } = this.state
    this.setState({
      point: [point[0], displayValue]
    })
  }

  async mapSpecification(): Promise<MapSpecification> {
    const settings = await this.settingsManager.getValues([SETTINGS.GEO_TILES, SETTINGS.GEO_API_KEY])
    return {
      type: settings[SETTINGS.GEO_TILES],
      key: settings[SETTINGS.GEO_API_KEY]
    }
  }

  panStrategy({ width, height, zoom, center }: PanStrategyArgs): { zoom: ?number, center: Point } {
    const { point } = this.state
    if (!point) return { zoom, center }
    if (!point[0] && !point[1]) return { zoom, center }
    return {
      zoom: 17,
      center: [CAST.Number(point[0]), CAST.Number(point[1])]
    }
  }

  dmsToDecimal(deg: string, min: string, sec: string, direction: string): number {
    let decimal = parseFloat(deg) + (parseFloat(min || 0) / 60) + (parseFloat(sec || 0) / 3600);
    if (direction && ['S', 'Ю', 'Y'].includes(direction.toUpperCase())) decimal *= -1;
    if (direction && ['W', 'З', 'Z'].includes(direction.toUpperCase())) decimal *= -1;
    return decimal;
  }


  parseCoordinatesFromClipBoard(text: string): ?LocationPoint {
    const results = [];

    const simpleMatch = text.match(
      /([-+]?\d{1,2}(?:\.\d+))\s*[,; ]\s*([-+]?\d{1,3}(?:\.\d+))/
    );

    if (simpleMatch) {
      const lat = parseFloat(simpleMatch[1]);
      const lon = parseFloat(simpleMatch[2]);
      if (!isNaN(lat) && !isNaN(lon)) {
        return {
          latitude: parseFloat(lat.toFixed(6)),
          longitude: parseFloat(lon.toFixed(6))
        };
      }
    }

    const normalizedText = text.replace(/,/g, '.');

    const decimalLatPattern = /([\-+]?\d{1,2}(?:[.]\d+))\s*(?:[°º]?)\s*(?:с\.?\s*ш\.?|северной широты|широты|N|n|ш)/gi;
    const decimalLonPattern = /([\-+]?\d{1,3}(?:[.]\d+))\s*(?:[°º]?)\s*(?:в\.?\s*д\.?|восточной долготы|долготы|E|e|д)/gi;

    const dmsPattern = /(\d{1,3})[°º\s]+(\d{1,2})[′']?\s*(?:(\d{1,2}(?:[.]\d+)?)["″]?)?\s*(с\.?\s*ш\.?|в\.?\s*д\.?|[NSEWЮСВЗ])/gi;
    const wordsPattern = /(\d{1,3})\s*град(?:ус|усов|уса)?\s*(\d{1,2})\s*мин(?:ут)?(?:а)?\s*(\d{1,2})\s*сек(?:унд)?\s*(северной|восточной|южной|западной)\s*(широты|долготы)/gi;

    let match: null | Array<string>;

    while ((match = decimalLatPattern.exec(normalizedText)) !== null) {
      if (match) results.push(parseFloat(parseFloat(match[1]).toFixed(6)))
    }
    while ((match = decimalLonPattern.exec(normalizedText)) !== null) {
      if (match) results.push(parseFloat(parseFloat(match[1]).toFixed(6)))
    }

    while ((match = dmsPattern.exec(normalizedText)) !== null) {
      if (match) {
        const [_, deg, min, sec, dirRaw] = match;
        const dir = dirRaw[0].toUpperCase();
        results.push(this.dmsToDecimal(deg, min, sec, dir))
      }
    }

    while ((match = wordsPattern.exec(normalizedText)) !== null) {
      if (match) {
        const [_, deg, min, sec, dirWord, type] = match;
        const dirMap = {
          северной: 'N',
          восточной: 'E',
          южной: 'S',
          западной: 'W'
        };
        const dir = dirMap[dirWord.toLowerCase()];
        results.push(this.dmsToDecimal(deg, min, sec, dir));
      }
    }

    if (results.length >= 2) {
      return {
        latitude: results[0],
        longitude: results[1]
      };
    }

    return null;
  }


  async handlePaste() {
    try {
      const allowed = ["text/plain", "text/html"]
      const clipboardContents = await navigator.clipboard.read();
      // $FlowFixMe[prop-missing]
      for (const item of clipboardContents) {
        for (const mimeType of item.types) {
          if (allowed.includes(mimeType)) {
            const blob = await item.getType(mimeType)
            const blobText = await blob.text();
            const coordinates = this.parseCoordinatesFromClipBoard(String(blobText))
            if (coordinates) {
              this.setState({
                point: [
                  (coordinates.latitude || 0).toFixed(6),
                  (coordinates.longitude || 0).toFixed(6)
                ]
              })
              return
            }
          }
        }
      }
      throw Error(__("UNABLE_TO_PARSE_COORDINATES"))
    } catch (e) {
      this.logger.error(e)
      const message = extractErrorMessage(e)
      await this.notificationManager.show({
        message,
        type: NOTIFICATION.ERROR
      })
    } 
  }

  async handleCopy () : Promise<void> {
    try {
      const { point } = this.state
      const [lat, lng] = point
      const data = [lat, lng].join(', ')
      await copyTextToClipboard(data)
      await this.notificationManager.show({        
        message: __mf('COPIED_TO_CLIPBOARD', { data }),
        type: NOTIFICATION.SUCCESS
      })
    } catch (e) {
      this.logger.error(e)
      const message = extractErrorMessage(e)
      await this.notificationManager.show({
        message,
        type: NOTIFICATION.ERROR
      })
    }
  }

  render(): React.Node {
    const { center } = this.props
    const { point, pointData } = this.state
    const markerPosition = [CAST.Number(point[0]), CAST.Number(point[1])]

    const { street, house } = pointData || {}
    const details = [street, house].join(' ').trim() || __('UNKNOWN_ADDRESS')
    return (
      <div className='h100 vertical-flex'>
        <Row className='h100'>
          <Col className='h400px' size={12}>
            <Map specification={this.mapSpecification} center={center} onClick={this.handleClick} panStrategy={this.panStrategy}>
              {(props) => (
                <>
                  {
                    markerPosition
                      ? (<PointMarker {...props} point={markerPosition} name='⇣' details={details} />)
                      : null
                  }
                </>
              )}
            </Map>
          </Col>
        </Row>
        <Row className='half-padding'>
          <Col size={3} className='left-label a-v'>
            {__('LATITUDE')}
          </Col>
          <Col size={3} className='a-v'>
            <NumericField
              ref={this.latitudeInput}
              hideInlineCalculator
              limit={LATITUDE_LIMITS}
              displayValue={point[0]}
              onChange={this.handleLatitudeChange}
            />
          </Col>
          <Col size={3} className='left-label a-v'>
            {__('LONGITUDE')}
          </Col>
          <Col size={3} className='a-v'>
            <NumericField
              hideInlineCalculator
              limit={LONGITUDE_LIMITS}
              displayValue={point[1]}
              onChange={this.handleLongitudeChange}
            />
          </Col>          
        </Row>
      </div>
    )
  }
}

export default LocationView
